import React, { lazy, memo } from 'react';
import styles from './CommonSettings.module.scss';
import InputSkeleton from '../../../../skeleton_loaders/common/InputSkeleton.jsx';
import withSuspense from '../../../../helpers/withSuspense.jsx';
import { useSelector, useStore } from 'react-redux';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import CommonSlider from '../../../common/CommonSlider.jsx';
import { widgetsNoTitle } from '../../WidgetLibraryGen2.jsx';
import { merge } from 'lodash';
import ToggleSwitch from '../../../common/ToggleSwitch.jsx';
import DashboardColorPicker from '../../../common/ColorPicker/DashboardColorPicker.jsx';

const InputSkeletonStyled = <InputSkeleton className={styles['settings-option']} />
const NumberInput = withSuspense(lazy(() => import('../../../common/NumberInput.jsx')), InputSkeletonStyled);
const Select = withSuspense(lazy(() => import('../../../common/Select.jsx')), InputSkeletonStyled);

/**
 * Common settings used by all widgets.
 * 
 * TODO: Color pickers can't have null values at the moment, which is problematic for overriding themes.
 * Probably should create new color pickers altogether that allow for this.
 */

const DEFAULTS = {
  titleColor: '#2D2D31',
  titleSize: 16,
  backgroundColor: '#FFFFFF',
  borderWidth: 1,
  titleFontWeight: 500,
  borderColor: '#E5E5E5',
  padding: {
    top: 10,
    right: 10,
    left: 10,
    bottom: 10
  }
};
function CommonSettings({ widgetId }) {
  const theme = useSelector(state => state.dashboard.theme);
  const store = useStore();
  const setAttribute = useSetWidgetAttribute(widgetId)
  const widgetType = useSelector(state => state.dashboard.widgets[widgetId].widget_type);
  const titleColor = useSelector(state => state.dashboard.widgets[widgetId].theme?.overrides?.common?.title?.color) ?? DEFAULTS.titleColor;
  const backgroundColor = useSelector(state => state.dashboard.widgets[widgetId].theme?.params?.backgroundColor) ?? DEFAULTS.backgroundColor;
  const borderColor = useSelector(state => state.dashboard.widgets[widgetId].theme?.params?.borderColor) ?? DEFAULTS.borderColor;
  const titleFontWeight = useSelector(state => state.dashboard.widgets[widgetId].theme?.overrides?.common?.title?.fontWeight) ?? DEFAULTS.titleFontWeight;
  const titleSize = useSelector(state => state.dashboard.widgets[widgetId].theme?.overrides?.common?.title?.fontSize) ?? DEFAULTS.titleSize;
  const padding = useSelector(state => state.dashboard.widgets[widgetId].theme?.overrides?.common?.padding) || DEFAULTS.padding;
  const borderRadius = useSelector(state => state.dashboard.widgets[widgetId].theme?.params?.borderRadius) ?? 'default';
  const borderWidth = useSelector(state => state.dashboard.widgets[widgetId].theme?.params?.borderWidth) ?? DEFAULTS.borderWidth;
  const useGlobalTheme = useSelector(state => state.dashboard.widgets[widgetId].settings?.useGlobalTheme) ?? false;

  const toggleGlobalTheme = () => {
    if (!useGlobalTheme && window.confirm(I18n.t('confirm_use_global_theme'))){
      const state = store.getState();
      setAttribute('settings.useGlobalTheme', true)
      setAttribute('theme', merge({}, state.dashboard.widgets[widgetId].theme || {}, state.dashboard.theme || {}))
    } else if (useGlobalTheme){
      setAttribute('settings.useGlobalTheme', false)
    }
  }
  return (
    <>
      <hr />
      <CollapsibleSection title={I18n.t('appearance_style')}>
      <div className={`${styles['settings-section']} flex-dir-row justify-content-space-between mb-1`}>
        <label>{I18n.t('use_global_theme')}</label>
        <ToggleSwitch name='is_selection' isOn={useGlobalTheme} defaultIsOn={useGlobalTheme} handleToggle={toggleGlobalTheme} />
      </div>
      <div className={styles['settings-section']}>
        {!widgetsNoTitle.includes(widgetType) && <>
          <div className={styles['settings-option']}>
            <label>{I18n.t('title_text_color')}</label>
            <DashboardColorPicker value={titleColor} onChange={(value) => setAttribute('theme.overrides.common.title.color', value)} />
          </div>
          <div className={styles['settings-option']}>
            <label>{I18n.t('text_size')}</label>
            <NumberInput id={`widget-text-size-${widgetId}`} type='number' value={titleSize} afterChange={(value) => setAttribute('theme.overrides.common.title.fontSize', value)} defaultValue={DEFAULTS.titleSize} />
          </div>
          <div className={styles['settings-option']}>
            <label>{I18n.t('title_font_weight')}</label>
            <CommonSlider value={titleFontWeight} min={100} max={900} step={100} onChange={(value) => setAttribute('theme.overrides.common.title.fontWeight', value)} />
          </div>
        </>}
        <div className={styles['settings-option']}>
          <label>{I18n.t('widget_background_color')}</label>
          <DashboardColorPicker value={backgroundColor} onChange={(value) => setAttribute('theme.params.backgroundColor', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('widget_border_color')}</label>
          <DashboardColorPicker value={borderColor} onChange={(value) => setAttribute('theme.params.borderColor', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('border_width')}</label>
          <CommonSlider value={borderWidth} min={1} max={5} onChange={(value) => setAttribute('theme.params.borderWidth', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('border_radius')}</label>
          <Select 
            options={[{value: 'default', label: I18n.t('default')}, {value: '0', label: I18n.t('square')}, {value: '15', label: I18n.t('rounded')}]} 
            value={borderRadius}
            inputId={`widget-card-corner-${widgetId}`}
            onChange={(value) => setAttribute('theme.params.borderRadius',value === 'default' ? null : Number(value))} 
          />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('padding_top')}</label>
          <CommonSlider value={padding.top} defaultValue={DEFAULTS.padding.top} min={0} max={50} onChange={(value) => setAttribute('theme.overrides.common.padding.top', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('padding_left')}</label>
          <CommonSlider value={padding.left} defaultValue={DEFAULTS.padding.left} min={0} max={50} onChange={(value) => setAttribute('theme.overrides.common.padding.left', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('padding_right')}</label>
          <CommonSlider value={padding.right} defaultValue={DEFAULTS.padding.right} min={0} max={50} onChange={(value) => setAttribute('theme.overrides.common.padding.right', value)} />
        </div>
        <div className={styles['settings-option']}>
          <label>{I18n.t('padding_bottom')}</label>
          <CommonSlider value={padding.bottom} defaultValue={DEFAULTS.padding.bottom} min={0} max={50} onChange={(value) => setAttribute('theme.overrides.common.padding.bottom', value)} />
        </div>
        {/* Forcing theme input to exist so that empty settings may be submitted */}
        <input type="hidden" name="theme[_exists]" value="1" />
      </div>
      </CollapsibleSection>
    </>
  );
};

export default memo(CommonSettings);